import { Stack, Typography, Box } from "@mui/material";
import { LgTextMd } from "Components/text/LgText";
import { COLORS } from "constants/layout";
import { SEARCH_PARAMS } from "constants/routes";
import { useQueryParam } from "helpers/search";
import useGetData from "hooks/useGetData";
import { useTranslation } from "hooks/useTranslation";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { apiGetSponsoredSuppliers } from "services/api/listing";
import { ParamsType } from "types/params";
import { ActivityTagType, SupplierListingItem } from "types/supplier";
import FullySupplierSkeleton from "Components/skeleton/FullySupplierSkeleton";
import ListingItemCard from "Components/card/SupplierListing";

type Props = {
  isPageDetail?: boolean;
  isNotShowButton?: boolean;
};

const SponsorListingSupplier = (props: Props) => {
  const t = useTranslation();
  const { isPageDetail, isNotShowButton } = props;
  const searchKeyword = useQueryParam(SEARCH_PARAMS.keyword);
  const { categoryId } = useParams<ParamsType>();
  const [renderError, setRenderError] = useState<Error | null>(null);

  // Helper to ensure value is always an array of strings
  const toStringArray = (val: any) => {
    if (Array.isArray(val)) return val.map(String);
    if (val !== undefined && val !== null && val !== "") return [String(val)];
    return [];
  };

  // Build filters object for the body
  const filters = {
    is_verified: toStringArray(undefined), // Replace with actual filter if available
    countries: toStringArray(undefined), // Replace with actual filter if available
    premium: toStringArray(undefined), // Replace with actual filter if available
    trust_star: toStringArray(undefined), // Replace with actual filter if available
  };

  // Build body object for POST
  const body = {
    category_id: categoryId || "",
    country: "", // Add country if available
    offset: 0, // Add offset if needed
    limit: 10, // Set a default or dynamic limit
    keyword: searchKeyword || "",
    sort: "hot", // Set sort if needed
    filters,
  };

  // API Call
  const { isLoading, data, error } = useGetData({
    promise: () => apiGetSponsoredSuppliers(body),
    deps: [searchKeyword, categoryId],
  });
  
  // Process data from API response with extra safety checks
  const suppliers: SupplierListingItem[] = useMemo(() => {
    try {
      if (!data) return [];
      
      let items: any[] = [];
      
      // Handle multiple possible response formats with null checks
      if (
        data.message &&
        data.message.data &&
        Array.isArray(data.message.data)
      ) {
        items = data.message.data;
      } else if (data.data && Array.isArray(data.data)) {
        items = data.data;
      } else if (Array.isArray(data)) {
        items = data;
      }
      
      // Normalize items - transform to match exactly what ListingItemCard needs
      const normalizedItems = items
        .filter((item) => item !== null && item !== undefined)
        .map(item => {
          // Create proper activity_tags structure if it's null
          let activityTags: ActivityTagType[] = [];
          if (Array.isArray(item.activity_tags)) {
            activityTags = item.activity_tags;
          }
          
          return {
            ...item,
            // Normalize required fields
            activity_tags: activityTags,
            gallery_preview: Array.isArray(item.gallery_preview) ? item.gallery_preview : [],
            export_categories: Array.isArray(item.export_categories) ? item.export_categories : [],
            attributes: Array.isArray(item.attributes) ? item.attributes : [],
            
            // Ensure all number properties have defaults
            trust_star: item.trust_star || 0,
            count_review: item.count_review || 0,
            count_inquiry: item.count_inquiry || 0,
            
            // Ensure boolean-like properties exist
            is_biz_verified: item.is_biz_verified || 0,
            is_verified_work_email: item.is_verified_work_email || 0,
            is_featured: item.is_featured || 0,
            is_valid_plan: item.is_valid_plan || 0,
            verified: item.verified || 0,
            
            // Ensure string fields aren't undefined
            farm_path: item.farm_path || '',
            farm_name: item.farm_name || '',
            country: item.country || '',
            country_code: item.country_code || '',
            plan_name: item.plan_name || 'Free'
          };
        });
      
      return normalizedItems;
    } catch (e) {
      console.error("Error processing supplier data:", e);
      setRenderError(e instanceof Error ? e : new Error(String(e)));
      return [];
    }
  }, [data]);

  // If no data and not loading, don't render anything - with extra checks
  if (!Array.isArray(suppliers) || (suppliers.length === 0 && !isLoading && !renderError)) {
    return null;
  }

  // Create a basic container for all cases
  return (
    <Stack
      p={{ xs: 1, md: 2 }}
      spacing={1}
      bgcolor={COLORS.yellow[50]}
      borderRadius={3}
      padding={"12px !important"}
      maxWidth={"100%"}
      className="sponsor_suppliers"
    >
      <LgTextMd>{t("sponsor_suppliers")}</LgTextMd>
      
      {/* Skeletons when loading */}
      {isLoading && 
        Array.from(new Array(2)).map((_, index) => (
          <FullySupplierSkeleton key={index} />
        ))}
      
      {/* Error state - Show raw data for debugging */}
      {renderError && data && (
        <Box p={2} bgcolor={COLORS.red[50]} borderRadius={1}>
          <Typography color="error" variant="subtitle2" gutterBottom>
            Error rendering suppliers: {renderError.message}
          </Typography>
          <Typography variant="caption">Raw data preview:</Typography>
          <Box 
            component="pre" 
            sx={{ 
              fontSize: '10px', 
              overflow: 'auto', 
              maxHeight: '200px',
              bgcolor: 'rgba(0,0,0,0.05)',
              p: 1, 
              borderRadius: 1 
            }}
          >
            {JSON.stringify(data.message?.data?.[0] || data, null, 2).slice(0, 500) + '...'}
          </Box>
        </Box>
      )}
      
      {/* Items after loading completes - Try/catch for each item to prevent complete failure */}
      {!isLoading &&
        Array.isArray(suppliers) &&
        suppliers.map((item: SupplierListingItem, index: number) => {
          try {
            return item ? (
              <div key={index}>
                <ListingItemCard
                  item={item}
                  isPageDetail={isPageDetail}
                  isNotShowButton={isNotShowButton}
                />
              </div>
            ) : null;
          } catch (err) {
            console.error(`Error rendering item ${index}:`, err);
            // Fallback to show raw item data for debugging
            return (
              <Box key={index} p={1} bgcolor={COLORS.red[50]} borderRadius={1}>
                <Typography variant="caption">Error rendering item {index}</Typography>
                <Box 
                  component="pre" 
                  sx={{ 
                    fontSize: '10px', 
                    overflow: 'auto', 
                    maxHeight: '100px',
                    bgcolor: 'rgba(0,0,0,0.05)', 
                    p: 1, 
                    borderRadius: 1 
                  }}
                >
                  {JSON.stringify(item, null, 2).slice(0, 300) + '...'}
                </Box>
              </Box>
            );
          }
        })}
    </Stack>
  );
};

export default SponsorListingSupplier;
